
import {
  USER_HOME,
  createDirectory,
  deleteEntry,
  renameEntry,
  uploadFiles,
} from "../api/files.js";
import {
  AGENT_HOME,
  createWorkspaceDirectory,
  deleteWorkspaceEntry,
  uploadFiles as uploadWorkspaceFiles,
} from "../api/threads.js";
import { files } from "../stores/file-state.js";
import { AppDataTable } from "./app-data-table.js";
import {
  bindMenuDismiss,
  browserLoad,
  closePreview,
  fileActions,
  fileName,
  handleFileDialogClose,
  handleFileMenuSelect,
  handleFileRowAction,
  handleSelectionChange,
  managerLoad,
  openMenu,
  reloadFileExplorer,
  restoreVersionAndReload,
  syncFileSelectionControls,
  type FileItem,
  type FileMenuTarget,
  type FileVersionItem,
  type VersionHost,
} from "./app-files-support.js";
import { explorerStyles, fileManagementStyles } from "./app-files-styles.js";
import { AppDialog } from "./app-dialog.js";
import { AppDropdownMenu } from "./app-dropdown-menu.js";
import { IconChevronLeft } from "./icons/chevron-left.js";
import { IconEllipsisVertical } from "./icons/ellipsis-vertical.js";
import { IconPlus } from "./icons/plus.js";
import { IconTrash2 } from "./icons/trash-2.js";
import { IconUpload } from "./icons/upload.js";

// Shared file-explorer body used by the /files page (global workspace, no
// `threadId`) and the thread side panel / mobile dialog (`threadId` set). The
// `fileActions()` adapter and the create/upload/remove calls branch on
// `threadId`; the render tree, menus, version and preview dialogs are shared.

function parentPath(path: string, root: string): string {
  if (path === root || !path.startsWith(`${root}/`)) return root;
  const parent = path.split("/").slice(0, -1).join("/");
  return parent.length < root.length ? root : parent;
}

export class AppFileExplorer extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppFileExplorer)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["threadid","data-thread-id","paneactive","data-pane-active","path","data-path","entries","data-entries","loading","data-loading","error","data-error","activefile","data-active-file","versions","data-versions","versionsopen","data-versions-open","versionsloading","data-versions-loading","previewopen","data-preview-open","previewurl","data-preview-url","previewtitle","data-preview-title","menuopen","data-menu-open","menutop","data-menu-top","menuleft","data-menu-left","menutarget","data-menu-target","actionitems","data-action-items"];

  #threadId = "";
  #paneActive = true;
  #path = USER_HOME;
  #entries = [];
  #loading = false;
  #error = "";
  #activeFile = null;
  #versions = [];
  #versionsOpen = false;
  #versionsLoading = false;
  #previewOpen = false;
  #previewUrl = "";
  #previewTitle = "";
  #menuOpen = false;
  #menuTop = 0;
  #menuLeft = 0;
  #menuTarget = null;
  #actionItems = [];
  #threadIdSet = false;
  #paneActiveSet = false;
  #pathSet = false;
  #entriesSet = false;
  #loadingSet = false;
  #errorSet = false;
  #activeFileSet = false;
  #versionsSet = false;
  #versionsOpenSet = false;
  #versionsLoadingSet = false;
  #previewOpenSet = false;
  #previewUrlSet = false;
  #previewTitleSet = false;
  #menuOpenSet = false;
  #menuTopSet = false;
  #menuLeftSet = false;
  #menuTargetSet = false;
  #actionItemsSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #textStart4;
  #textEnd4;
  #textLast4;
  #textStart5;
  #textEnd5;
  #textLast5;
  #textStart6;
  #textEnd6;
  #textLast6;
  #textStart7;
  #textEnd7;
  #textLast7;
  #textStart8;
  #textEnd8;
  #textLast8;
  #textStart9;
  #textEnd9;
  #textLast9;
  #textStart10;
  #textEnd10;
  #textLast10;
  #textStart11;
  #textEnd11;
  #textLast11;
  #textStart12;
  #textEnd12;
  #textLast12;
  #attr0;
  #attr1;
  #attr2;
  #attr3;
  #attr4;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #attrLast3;
  #attrLast4;
  #attrLast5;
  #attrLast6;
  #attrLast7;
  #attrLast8;
  #attrLast9;
  #attrLast10;
  #attrLast11;
  #attrLast12;
  #storeCleanups = [];
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get threadId() {
    return this.#threadId;
  }

  set threadId(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#threadId, next)) return;
    this.#threadId = next;
    this.#threadIdSet = true;
    this.#updateBindings('threadId');
  }

  get paneActive() {
    return this.#paneActive;
  }

  set paneActive(value) {
    const raw = value ?? true;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#paneActive, next)) return;
    this.#paneActive = next;
    this.#paneActiveSet = true;
    this.#updateBindings('paneActive');
  }

  get path() {
    return this.#path;
  }

  set path(value) {
    const raw = value ?? USER_HOME;
    const next = raw == null ? raw : raw;
    if (Object.is(this.#path, next)) return;
    this.#path = next;
    this.#pathSet = true;
    this.#updateBindings('path');
  }

  get entries() {
    return this.#entries;
  }

  set entries(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#entries, next)) return;
    this.#entries = next;
    this.#entriesSet = true;
    this.#updateBindings('entries');
  }

  get loading() {
    return this.#loading;
  }

  set loading(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#loading, next)) return;
    this.#loading = next;
    this.#loadingSet = true;
    this.#updateBindings('loading');
  }

  get error() {
    return this.#error;
  }

  set error(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#error, next)) return;
    this.#error = next;
    this.#errorSet = true;
    this.#updateBindings('error');
  }

  get activeFile() {
    return this.#activeFile;
  }

  set activeFile(value) {
    const raw = value ?? null;
    const next = raw == null ? raw : raw;
    if (Object.is(this.#activeFile, next)) return;
    this.#activeFile = next;
    this.#activeFileSet = true;
    this.#updateBindings('activeFile');
  }

  get versions() {
    return this.#versions;
  }

  set versions(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#versions, next)) return;
    this.#versions = next;
    this.#versionsSet = true;
    this.#updateBindings('versions');
  }

  get versionsOpen() {
    return this.#versionsOpen;
  }

  set versionsOpen(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#versionsOpen, next)) return;
    this.#versionsOpen = next;
    this.#versionsOpenSet = true;
    this.#updateBindings('versionsOpen');
  }

  get versionsLoading() {
    return this.#versionsLoading;
  }

  set versionsLoading(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#versionsLoading, next)) return;
    this.#versionsLoading = next;
    this.#versionsLoadingSet = true;
    this.#updateBindings('versionsLoading');
  }

  get previewOpen() {
    return this.#previewOpen;
  }

  set previewOpen(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#previewOpen, next)) return;
    this.#previewOpen = next;
    this.#previewOpenSet = true;
    this.#updateBindings('previewOpen');
  }

  get previewUrl() {
    return this.#previewUrl;
  }

  set previewUrl(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#previewUrl, next)) return;
    this.#previewUrl = next;
    this.#previewUrlSet = true;
    this.#updateBindings('previewUrl');
  }

  get previewTitle() {
    return this.#previewTitle;
  }

  set previewTitle(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#previewTitle, next)) return;
    this.#previewTitle = next;
    this.#previewTitleSet = true;
    this.#updateBindings('previewTitle');
  }

  get menuOpen() {
    return this.#menuOpen;
  }

  set menuOpen(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#menuOpen, next)) return;
    this.#menuOpen = next;
    this.#menuOpenSet = true;
    this.#updateBindings('menuOpen');
  }

  get menuTop() {
    return this.#menuTop;
  }

  set menuTop(value) {
    const raw = value ?? 0;
    const next = raw == null ? raw : Number(raw);
    if (Object.is(this.#menuTop, next)) return;
    this.#menuTop = next;
    this.#menuTopSet = true;
    this.#updateBindings('menuTop');
  }

  get menuLeft() {
    return this.#menuLeft;
  }

  set menuLeft(value) {
    const raw = value ?? 0;
    const next = raw == null ? raw : Number(raw);
    if (Object.is(this.#menuLeft, next)) return;
    this.#menuLeft = next;
    this.#menuLeftSet = true;
    this.#updateBindings('menuLeft');
  }

  get menuTarget() {
    return this.#menuTarget;
  }

  set menuTarget(value) {
    const raw = value ?? null;
    const next = raw == null ? raw : raw;
    if (Object.is(this.#menuTarget, next)) return;
    this.#menuTarget = next;
    this.#menuTargetSet = true;
    this.#updateBindings('menuTarget');
  }

  get actionItems() {
    return this.#actionItems;
  }

  set actionItems(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#actionItems, next)) return;
    this.#actionItems = next;
    this.#actionItemsSet = true;
    this.#updateBindings('actionItems');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-thread-id')) this.threadId = this.dataset.threadId;
    if (this.hasAttribute('data-pane-active')) this.paneActive = this.dataset.paneActive;
    if (this.hasAttribute('data-path')) this.path = this.dataset.path;
    if (this.hasAttribute('data-entries')) this.entries = this.dataset.entries;
    if (this.hasAttribute('data-loading')) this.loading = this.dataset.loading;
    if (this.hasAttribute('data-error')) this.error = this.dataset.error;
    if (this.hasAttribute('data-active-file')) this.activeFile = this.dataset.activeFile;
    if (this.hasAttribute('data-versions')) this.versions = this.dataset.versions;
    if (this.hasAttribute('data-versions-open')) this.versionsOpen = this.dataset.versionsOpen;
    if (this.hasAttribute('data-versions-loading')) this.versionsLoading = this.dataset.versionsLoading;
    if (this.hasAttribute('data-preview-open')) this.previewOpen = this.dataset.previewOpen;
    if (this.hasAttribute('data-preview-url')) this.previewUrl = this.dataset.previewUrl;
    if (this.hasAttribute('data-preview-title')) this.previewTitle = this.dataset.previewTitle;
    if (this.hasAttribute('data-menu-open')) this.menuOpen = this.dataset.menuOpen;
    if (this.hasAttribute('data-menu-top')) this.menuTop = this.dataset.menuTop;
    if (this.hasAttribute('data-menu-left')) this.menuLeft = this.dataset.menuLeft;
    if (this.hasAttribute('data-menu-target')) this.menuTarget = this.dataset.menuTarget;
    if (this.hasAttribute('data-action-items')) this.actionItems = this.dataset.actionItems;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.threadId = props.threadId;
      this.paneActive = props.paneActive;
      this.path = props.path;
      this.entries = props.entries;
      this.loading = props.loading;
      this.error = props.error;
      this.activeFile = props.activeFile;
      this.versions = props.versions;
      this.versionsOpen = props.versionsOpen;
      this.versionsLoading = props.versionsLoading;
      this.previewOpen = props.previewOpen;
      this.previewUrl = props.previewUrl;
      this.previewTitle = props.previewTitle;
      this.menuOpen = props.menuOpen;
      this.menuTop = props.menuTop;
      this.menuLeft = props.menuLeft;
      this.menuTarget = props.menuTarget;
      this.actionItems = props.actionItems;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#threadIdSet) this.threadId = this.dataset.threadId;
    if (!this.#paneActiveSet) this.paneActive = this.dataset.paneActive;
    if (!this.#pathSet) this.path = this.dataset.path;
    if (!this.#entriesSet) this.entries = this.dataset.entries;
    if (!this.#loadingSet) this.loading = this.dataset.loading;
    if (!this.#errorSet) this.error = this.dataset.error;
    if (!this.#activeFileSet) this.activeFile = this.dataset.activeFile;
    if (!this.#versionsSet) this.versions = this.dataset.versions;
    if (!this.#versionsOpenSet) this.versionsOpen = this.dataset.versionsOpen;
    if (!this.#versionsLoadingSet) this.versionsLoading = this.dataset.versionsLoading;
    if (!this.#previewOpenSet) this.previewOpen = this.dataset.previewOpen;
    if (!this.#previewUrlSet) this.previewUrl = this.dataset.previewUrl;
    if (!this.#previewTitleSet) this.previewTitle = this.dataset.previewTitle;
    if (!this.#menuOpenSet) this.menuOpen = this.dataset.menuOpen;
    if (!this.#menuTopSet) this.menuTop = this.dataset.menuTop;
    if (!this.#menuLeftSet) this.menuLeft = this.dataset.menuLeft;
    if (!this.#menuTargetSet) this.menuTarget = this.dataset.menuTarget;
    if (!this.#actionItemsSet) this.actionItems = this.dataset.actionItems;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    this.#connectStores();
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    if (!this.threadId)
        void browserLoad(this);
    return () => closePreview(this as VersionHost);
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
    this.#effect0();
    this.#effect1();
    this.#effect2();
  }

  disconnectedCallback() {
    this.#disconnectStores();
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
    if (this.#effect1Cleanup) { this.#effect1Cleanup(); this.#effect1Cleanup = undefined; }
    if (this.#effect2Cleanup) { this.#effect2Cleanup(); this.#effect2Cleanup = undefined; }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'threadid':
      case 'data-thread-id':
        this.threadId = newValue;
        break;
      case 'paneactive':
      case 'data-pane-active':
        this.paneActive = newValue;
        break;
      case 'path':
      case 'data-path':
        this.path = newValue;
        break;
      case 'entries':
      case 'data-entries':
        this.entries = newValue;
        break;
      case 'loading':
      case 'data-loading':
        this.loading = newValue;
        break;
      case 'error':
      case 'data-error':
        this.error = newValue;
        break;
      case 'activefile':
      case 'data-active-file':
        this.activeFile = newValue;
        break;
      case 'versions':
      case 'data-versions':
        this.versions = newValue;
        break;
      case 'versionsopen':
      case 'data-versions-open':
        this.versionsOpen = newValue;
        break;
      case 'versionsloading':
      case 'data-versions-loading':
        this.versionsLoading = newValue;
        break;
      case 'previewopen':
      case 'data-preview-open':
        this.previewOpen = newValue;
        break;
      case 'previewurl':
      case 'data-preview-url':
        this.previewUrl = newValue;
        break;
      case 'previewtitle':
      case 'data-preview-title':
        this.previewTitle = newValue;
        break;
      case 'menuopen':
      case 'data-menu-open':
        this.menuOpen = newValue;
        break;
      case 'menutop':
      case 'data-menu-top':
        this.menuTop = newValue;
        break;
      case 'menuleft':
      case 'data-menu-left':
        this.menuLeft = newValue;
        break;
      case 'menutarget':
      case 'data-menu-target':
        this.menuTarget = newValue;
        break;
      case 'actionitems':
      case 'data-action-items':
        this.actionItems = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const createFolder = () => {
    const name = window.prompt("Folder name:")?.trim();
    if (!name)
        return;
    const target = [this.path, name].filter(Boolean).join("/");
    const request = this.threadId
        ? createWorkspaceDirectory(this.threadId, target)
        : createDirectory(target);
    void request.then(() => reloadFileExplorer(this as VersionHost)).catch(() => {
        this.error = "Failed to create folder.";
    });
};
    const removeSelected = () => {
    if (files.selected.length === 0)
        return;
    if (!window.confirm("Remove selected files?"))
        return;
    void (async () => {
        try {
            for (const target of files.selected) {
                await (this.threadId ? deleteWorkspaceEntry(this.threadId, target) : deleteEntry(target));
            }
            await reloadFileExplorer(this as VersionHost);
        }
        catch {
            this.error = "Failed to remove selected files.";
        }
    })();
};
    const uploadPicked = (event: Event) => {
    const picker = event.target as HTMLInputElement;
    const picked = Array.from(picker.files ?? []);
    picker.value = "";
    if (picked.length === 0)
        return;
    this.error = "";
    const request = this.threadId
        ? uploadWorkspaceFiles(this.threadId, picked, this.path)
        : uploadFiles(picked, this.path);
    void request.then(() => reloadFileExplorer(this as VersionHost)).catch(() => {
        this.error = "Upload failed.";
    });
};
    const renameSelected = () => {
    const pathToRename = files.selected[0];
    const entry = (this.entries as FileItem[]).find((item) => item.path === pathToRename);
    if (!entry)
        return;
    const name = window.prompt("New name:", entry.name)?.trim();
    if (!name || name === entry.name)
        return;
    void renameEntry(pathToRename, name).then(() => reloadFileExplorer(this as VersionHost)).catch(() => {
        this.error = "Failed to rename.";
    });
};
    const emptyText = this.#threadId ? "No files here." : "No files here yet. Upload to get started.";
    return `<style>${explorerStyles}</style><style>${fileManagementStyles}</style><div class="toolbar"><button class="action-button" type="button" data-tool="folder" data-argon="5978"><icon-plus><!--argon-text:0-->${""}<!--/argon-text:0--></icon-plus><span><!--argon-text:1-->${this.#escText(this.#threadId ? "Folder" : "New folder")}<!--/argon-text:1--></span></button><button class="action-button" type="button" data-tool="upload" data-argon="6203"><icon-upload><!--argon-text:2-->${""}<!--/argon-text:2--></icon-upload><span>Upload</span></button><!--argon-text:3-->${this.#threadId ? this.#escText("") : `<button class="action-button" type="button" data-tool="rename" data-argon="6471"><span>Rename</span></button>`}<!--/argon-text:3--><button class="action-button" type="button" data-tool="remove" data-argon="6631"><icon-trash2><!--argon-text:4-->${""}<!--/argon-text:4--></icon-trash2><span>Remove</span></button><input type="file" multiple data-argon="6756"></div><div class="path"><button class="icon-button" type="button" aria-label="Up one level" data-tool="up" data-argon="6961"><icon-chevron-left><!--argon-text:5-->${""}<!--/argon-text:5--></icon-chevron-left></button><span><!--argon-text:6-->${this.#escText(this.#path)}<!--/argon-text:6--></span></div><div class="error"><!--argon-text:7-->${this.#escText(this.#error)}<!--/argon-text:7--></div><app-data-table data-rows="${this.#attrEsc(JSON.stringify(this.#entries))}" data-selected="${this.#attrEsc(files.selected)}" data-loading="${this.#attrEsc(this.#loading)}" data-loading-text="Loading files..." data-empty-text="${this.#attrEsc(emptyText)}" data-argon="7523" data-argon-bind="0"><!--argon-text:8-->${""}<!--/argon-text:8--></app-data-table><app-dropdown-menu data-open="${this.#attrEsc(this.#menuOpen)}" data-items="${this.#attrEsc(this.#actionItems)}" data-position="${this.#attrEsc(`left:${this.#menuLeft}px;top:${this.#menuTop}px`)}" data-argon="8052" data-argon-bind="1"><!--argon-text:9-->${""}<!--/argon-text:9--></app-dropdown-menu><app-dialog data-dialog-id="versions" data-open="${this.#attrEsc(this.#versionsOpen)}" data-title="${this.#attrEsc(this.#activeFile ? fileName(this.#activeFile.path) : "File versions")}" data-description="Restore, download, or preview a saved version." data-argon="8362" data-argon-bind="2"><!--argon-text:10-->${""}<!--/argon-text:10--><div class="versions" data-argon="8498"><!--argon-text:11-->${this.#versionsLoading ? "<div class=\"dialog-empty\">Loading versions...</div>" : (this.#versions.length === 0) ? "<div class=\"dialog-empty\">No versions found.</div>" : this.#versions.map((version) => `<div class="version-row"><div class="version-main"><div class="version-title"><span>Version ${version.version}</span>${version.latest ? "<span class=\"badge\">Current</span>" : this.#escText("")}</div><div class="version-meta">${this.#escText(version.createdLabel)} · ${this.#escText(version.sizeLabel)}</div></div><button class="text-button" type="button" data-version-action="restore">Restore</button><button class="version-menu" type="button" aria-label="Version actions" data-version-action="menu"><icon-ellipsis-vertical>${""}</icon-ellipsis-vertical></button></div>`).join("")}<!--/argon-text:11--></div></app-dialog><app-dialog data-dialog-id="preview" data-open="${this.#attrEsc(this.#previewOpen)}" data-title="${this.#attrEsc(this.#previewTitle)}" data-size="wide" data-argon="10683" data-argon-bind="3"><!--argon-text:12-->${""}<!--/argon-text:12--><iframe class="preview-frame" src="${this.#escAttr(this.#previewUrl)}" title="${this.#escAttr(this.#previewTitle)}" data-argon-bind="4"></iframe></app-dialog>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
        case 'argon-text:4':
          this.#textStart4 = marker;
          break;
        case '/argon-text:4':
          this.#textEnd4 = marker;
          break;
        case 'argon-text:5':
          this.#textStart5 = marker;
          break;
        case '/argon-text:5':
          this.#textEnd5 = marker;
          break;
        case 'argon-text:6':
          this.#textStart6 = marker;
          break;
        case '/argon-text:6':
          this.#textEnd6 = marker;
          break;
        case 'argon-text:7':
          this.#textStart7 = marker;
          break;
        case '/argon-text:7':
          this.#textEnd7 = marker;
          break;
        case 'argon-text:8':
          this.#textStart8 = marker;
          break;
        case '/argon-text:8':
          this.#textEnd8 = marker;
          break;
        case 'argon-text:9':
          this.#textStart9 = marker;
          break;
        case '/argon-text:9':
          this.#textEnd9 = marker;
          break;
        case 'argon-text:10':
          this.#textStart10 = marker;
          break;
        case '/argon-text:10':
          this.#textEnd10 = marker;
          break;
        case 'argon-text:11':
          this.#textStart11 = marker;
          break;
        case '/argon-text:11':
          this.#textEnd11 = marker;
          break;
        case 'argon-text:12':
          this.#textStart12 = marker;
          break;
        case '/argon-text:12':
          this.#textEnd12 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#attr2 = root.querySelector('[data-argon-bind="2"]');
    this.#attr3 = root.querySelector('[data-argon-bind="3"]');
    this.#attr4 = root.querySelector('[data-argon-bind="4"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    const root = this.shadowRoot;
    if (!root || !this.#menuOpen)
        return;
    return bindMenuDismiss(this as VersionHost, root);
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }

  #effect1Cleanup;
  #effect1() {
    if (this.#effect1Cleanup) this.#effect1Cleanup();
    const cleanup = (() => {
    if (!this.#threadId)
        return;
    this.toggleAttribute("data-compact", true);
    if (this._loadedThread !== this.#threadId) {
        this._loadedThread = this.#threadId;
        this._loadedKey = "";
        this.path = AGENT_HOME;
        this.entries = [];
        files.selected = [];
    }
    if (!this.#paneActive)
        return;
    const key = `${this.#threadId}:${this.path}`;
    if (this._loadedKey === key)
        return;
    this._loadedKey = key;
    void managerLoad(this as unknown as ManagerHost);
})();
    this.#effect1Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }

  #effect2Cleanup;
  #effect2() {
    if (this.#effect2Cleanup) this.#effect2Cleanup();
    const cleanup = (() => {
    const root = this.shadowRoot!;
    const rootPath = this.#threadId ? AGENT_HOME : USER_HOME;
    syncFileSelectionControls(this as unknown as ManagerHost);
    root.querySelector('[data-tool="up"]')?.toggleAttribute("disabled", this.#path === rootPath);
})();
    this.#effect2Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #connectStores() {
    if (this.#storeCleanups.length > 0) return;
    this.#storeCleanups.push(__argonStoreSubscribe0("files.selected", () => this.#updateBindings("store:../stores/file-state.js:files.selected")));
  }

  #disconnectStores() {
    for (const cleanup of this.#storeCleanups.splice(0)) cleanup();
  }

  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const createFolder = () => {
    const name = window.prompt("Folder name:")?.trim();
    if (!name)
        return;
    const target = [this.path, name].filter(Boolean).join("/");
    const request = this.threadId
        ? createWorkspaceDirectory(this.threadId, target)
        : createDirectory(target);
    void request.then(() => reloadFileExplorer(this as VersionHost)).catch(() => {
        this.error = "Failed to create folder.";
    });
};
    const removeSelected = () => {
    if (files.selected.length === 0)
        return;
    if (!window.confirm("Remove selected files?"))
        return;
    void (async () => {
        try {
            for (const target of files.selected) {
                await (this.threadId ? deleteWorkspaceEntry(this.threadId, target) : deleteEntry(target));
            }
            await reloadFileExplorer(this as VersionHost);
        }
        catch {
            this.error = "Failed to remove selected files.";
        }
    })();
};
    const uploadPicked = (event: Event) => {
    const picker = event.target as HTMLInputElement;
    const picked = Array.from(picker.files ?? []);
    picker.value = "";
    if (picked.length === 0)
        return;
    this.error = "";
    const request = this.threadId
        ? uploadWorkspaceFiles(this.threadId, picked, this.path)
        : uploadFiles(picked, this.path);
    void request.then(() => reloadFileExplorer(this as VersionHost)).catch(() => {
        this.error = "Upload failed.";
    });
};
    const renameSelected = () => {
    const pathToRename = files.selected[0];
    const entry = (this.entries as FileItem[]).find((item) => item.path === pathToRename);
    if (!entry)
        return;
    const name = window.prompt("New name:", entry.name)?.trim();
    if (!name || name === entry.name)
        return;
    void renameEntry(pathToRename, name).then(() => reloadFileExplorer(this as VersionHost)).catch(() => {
        this.error = "Failed to rename.";
    });
};
    const emptyText = (prop === undefined || prop === 'entries' || prop === 'store:../stores/file-state.js:files.selected' || prop === 'loading' || prop === 'threadId') ? (this.#threadId ? "No files here." : "No files here yet. Upload to get started.") : undefined;
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'threadId') {
      const __argonNext = `${this.#escText(this.#threadId ? "Folder" : "New folder")}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined || prop === 'threadId') {
      const __argonNext = `${this.#threadId ? this.#escText("") : `<button class="action-button" type="button" data-tool="rename" data-argon="6471"><span>Rename</span></button>`}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast4) {
        this.#textLast4 = __argonNext;
        this.#replaceRange(this.#textStart4, this.#textEnd4, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast5) {
        this.#textLast5 = __argonNext;
        this.#replaceRange(this.#textStart5, this.#textEnd5, __argonNext);
      }
    }
    if (prop === undefined || prop === 'path') {
      const __argonNext = `${this.#escText(this.#path)}`;
      if (__argonNext !== this.#textLast6) {
        this.#textLast6 = __argonNext;
        this.#replaceRange(this.#textStart6, this.#textEnd6, __argonNext);
      }
    }
    if (prop === undefined || prop === 'error') {
      const __argonNext = `${this.#escText(this.#error)}`;
      if (__argonNext !== this.#textLast7) {
        this.#textLast7 = __argonNext;
        this.#replaceRange(this.#textStart7, this.#textEnd7, __argonNext);
      }
    }
    if (prop === undefined || prop === 'entries' || prop === 'store:../stores/file-state.js:files.selected' || prop === 'loading' || prop === 'threadId') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast8) {
        this.#textLast8 = __argonNext;
        this.#replaceRange(this.#textStart8, this.#textEnd8, __argonNext);
      }
    }
    if (prop === undefined || prop === 'menuOpen' || prop === 'actionItems' || prop === 'menuLeft' || prop === 'menuTop') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast9) {
        this.#textLast9 = __argonNext;
        this.#replaceRange(this.#textStart9, this.#textEnd9, __argonNext);
      }
    }
    if (prop === undefined || prop === 'versionsOpen' || prop === 'activeFile') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast10) {
        this.#textLast10 = __argonNext;
        this.#replaceRange(this.#textStart10, this.#textEnd10, __argonNext);
      }
    }
    if (prop === undefined || prop === 'versionsLoading' || prop === 'versions') {
      const __argonNext = `${this.#versionsLoading ? "<div class=\"dialog-empty\">Loading versions...</div>" : (this.#versions.length === 0) ? "<div class=\"dialog-empty\">No versions found.</div>" : this.#versions.map((version) => `<div class="version-row"><div class="version-main"><div class="version-title"><span>Version ${version.version}</span>${version.latest ? "<span class=\"badge\">Current</span>" : this.#escText("")}</div><div class="version-meta">${this.#escText(version.createdLabel)} · ${this.#escText(version.sizeLabel)}</div></div><button class="text-button" type="button" data-version-action="restore">Restore</button><button class="version-menu" type="button" aria-label="Version actions" data-version-action="menu"><icon-ellipsis-vertical>${""}</icon-ellipsis-vertical></button></div>`).join("")}`;
      if (__argonNext !== this.#textLast11) {
        this.#textLast11 = __argonNext;
        this.#replaceRange(this.#textStart11, this.#textEnd11, __argonNext);
      }
    }
    if (prop === undefined || prop === 'previewOpen' || prop === 'previewTitle') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast12) {
        this.#textLast12 = __argonNext;
        this.#replaceRange(this.#textStart12, this.#textEnd12, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'entries') && this.#attr0) {
      const __argonNext = `${this.#attrRaw(JSON.stringify(this.#entries))}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('data-rows', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/file-state.js:files.selected') && this.#attr0) {
      const __argonNext = `${this.#attrRaw(files.selected)}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr0.setAttribute('data-selected', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'loading') && this.#attr0) {
      const __argonNext = `${this.#attrRaw(this.#loading)}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr0.setAttribute('data-loading', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'threadId') && this.#attr0) {
      const __argonNext = `${this.#attrRaw(emptyText)}`;
      if (__argonNext !== this.#attrLast3) {
        this.#attrLast3 = __argonNext;
        this.#attr0.setAttribute('data-empty-text', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'menuOpen') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(this.#menuOpen)}`;
      if (__argonNext !== this.#attrLast4) {
        this.#attrLast4 = __argonNext;
        this.#attr1.setAttribute('data-open', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'actionItems') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(this.#actionItems)}`;
      if (__argonNext !== this.#attrLast5) {
        this.#attrLast5 = __argonNext;
        this.#attr1.setAttribute('data-items', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'menuLeft' || prop === 'menuTop') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(`left:${this.#menuLeft}px;top:${this.#menuTop}px`)}`;
      if (__argonNext !== this.#attrLast6) {
        this.#attrLast6 = __argonNext;
        this.#attr1.setAttribute('data-position', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'versionsOpen') && this.#attr2) {
      const __argonNext = `${this.#attrRaw(this.#versionsOpen)}`;
      if (__argonNext !== this.#attrLast7) {
        this.#attrLast7 = __argonNext;
        this.#attr2.setAttribute('data-open', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'activeFile') && this.#attr2) {
      const __argonNext = `${this.#attrRaw(this.#activeFile ? fileName(this.#activeFile.path) : "File versions")}`;
      if (__argonNext !== this.#attrLast8) {
        this.#attrLast8 = __argonNext;
        this.#attr2.setAttribute('data-title', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'previewOpen') && this.#attr3) {
      const __argonNext = `${this.#attrRaw(this.#previewOpen)}`;
      if (__argonNext !== this.#attrLast9) {
        this.#attrLast9 = __argonNext;
        this.#attr3.setAttribute('data-open', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'previewTitle') && this.#attr3) {
      const __argonNext = `${this.#attrRaw(this.#previewTitle)}`;
      if (__argonNext !== this.#attrLast10) {
        this.#attrLast10 = __argonNext;
        this.#attr3.setAttribute('data-title', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'previewUrl') && this.#attr4) {
      const __argonNext = `${this.#previewUrl}`;
      if (__argonNext !== this.#attrLast11) {
        this.#attrLast11 = __argonNext;
        this.#attr4.setAttribute('src', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'previewTitle') && this.#attr4) {
      const __argonNext = `${this.#previewTitle}`;
      if (__argonNext !== this.#attrLast12) {
        this.#attrLast12 = __argonNext;
        this.#attr4.setAttribute('title', __argonNext);
      }
    }
    if (this.#hooksLive && (prop === 'menuOpen')) this.#effect0();
    if (this.#hooksLive && (prop === 'threadId' || prop === 'paneActive')) this.#effect1();
    if (this.#hooksLive && (prop === 'threadId' || prop === 'path')) this.#effect2();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '5978').forEach(el => this.#listen(el, '5978', 'click', () => {
    const name = window.prompt("Folder name:")?.trim();
    if (!name)
        return;
    const target = [this.path, name].filter(Boolean).join("/");
    const request = this.threadId
        ? createWorkspaceDirectory(this.threadId, target)
        : createDirectory(target);
    void request.then(() => reloadFileExplorer(this as VersionHost)).catch(() => {
        this.error = "Failed to create folder.";
    });
}));
    this.#eventTargets(scope, '6203').forEach(el => this.#listen(el, '6203', 'click', () => this.shadowRoot!.querySelector<HTMLInputElement>('input[type="file"]')!.click()));
    this.#eventTargets(scope, '6631').forEach(el => this.#listen(el, '6631', 'click', () => {
    if (files.selected.length === 0)
        return;
    if (!window.confirm("Remove selected files?"))
        return;
    void (async () => {
        try {
            for (const target of files.selected) {
                await (this.threadId ? deleteWorkspaceEntry(this.threadId, target) : deleteEntry(target));
            }
            await reloadFileExplorer(this as VersionHost);
        }
        catch {
            this.error = "Failed to remove selected files.";
        }
    })();
}));
    this.#eventTargets(scope, '6756').forEach(el => this.#listen(el, '6756', 'change', (event: Event) => {
    const picker = event.target as HTMLInputElement;
    const picked = Array.from(picker.files ?? []);
    picker.value = "";
    if (picked.length === 0)
        return;
    this.error = "";
    const request = this.threadId
        ? uploadWorkspaceFiles(this.threadId, picked, this.path)
        : uploadFiles(picked, this.path);
    void request.then(() => reloadFileExplorer(this as VersionHost)).catch(() => {
        this.error = "Upload failed.";
    });
}));
    this.#eventTargets(scope, '6961').forEach(el => this.#listen(el, '6961', 'click', () => {
    const root = this.threadId ? AGENT_HOME : USER_HOME;
    this.path = parentPath(this.path as string, root);
    void reloadFileExplorer(this as VersionHost);
}));
    this.#eventTargets(scope, '7523').forEach(el => this.#listen(el, '7523', 'selection-change', (event: Event) => {
    handleSelectionChange(event);
    syncFileSelectionControls(this as unknown as ManagerHost);
}));
    this.#eventTargets(scope, '7523').forEach(el => this.#listen(el, '7523', 'row-action', (event: Event) => handleFileRowAction(this as VersionHost, event, (entry) => {
    this.path = entry.path;
    void reloadFileExplorer(this as VersionHost);
})));
    this.#eventTargets(scope, '8052').forEach(el => this.#listen(el, '8052', 'select', (event: Event) => handleFileMenuSelect(this as VersionHost, event)));
    this.#eventTargets(scope, '8362').forEach(el => this.#listen(el, '8362', 'close', (event: Event) => handleFileDialogClose(this as VersionHost, event)));
    this.#eventTargets(scope, '8498').forEach(el => this.#listen(el, '8498', 'click', (event: MouseEvent) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-version-action]");
    if (!button)
        return;
    const row = button.closest(".version-row");
    const index = row ? Array.from(row.parentElement?.children ?? []).indexOf(row) : -1;
    const version = (this.versions as FileVersionItem[])[index]?.version;
    if (version == null)
        return;
    if (button.dataset.versionAction === "restore") {
        void restoreVersionAndReload(this as VersionHost, fileActions(this as VersionHost), version);
    }
    else if (button.dataset.versionAction === "menu" && this.#activeFile) {
        openMenu(this as VersionHost, event, { kind: "version", path: this.#activeFile.path, version });
    }
}));
    this.#eventTargets(scope, '10683').forEach(el => this.#listen(el, '10683', 'close', (event: Event) => handleFileDialogClose(this as VersionHost, event)));
    this.#eventTargets(scope, '6471').forEach(el => this.#listen(el, '6471', 'click', () => {
    const pathToRename = files.selected[0];
    const entry = (this.entries as FileItem[]).find((item) => item.path === pathToRename);
    if (!entry)
        return;
    const name = window.prompt("New name:", entry.name)?.trim();
    if (!name || name === entry.name)
        return;
    void renameEntry(pathToRename, name).then(() => reloadFileExplorer(this as VersionHost)).catch(() => {
        this.error = "Failed to rename.";
    });
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
